import React from 'react';
import classNames from 'classnames';

import { pickInitialValuesForFieldSelectTree } from '../../../util/search';

import { FieldSelectTree, FieldSelect, FieldSearchableSelect } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectSingleFilter.module.css';

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

const getBranchPath = (primaryOptions, currentSelections) => {
  const currentSelectionsEntries = Object.entries(currentSelections).sort(([k, v]) => k);
  return currentSelectionsEntries.reduce((branchPath, entry) => {
    const [levelKey, selectedOption] = entry;

    const currentOptions =
      branchPath.length > 0 ? branchPath[branchPath.length - 1].suboptions : primaryOptions;
    const foundOption = currentOptions.find(o => o.option === selectedOption);
    const foundOptionWithLevelKeyMaybe = foundOption ? [{ ...foundOption, levelKey }] : [];

    return [...branchPath, ...foundOptionWithLevelKeyMaybe];
  }, []);
};

/**
 * SelectSingleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {React.ReactNode} props.label - The label
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Array<Object>} props.options - The options
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @param {boolean} [props.isNestedEnum] - Whether the enum is nested
 * @returns {JSX.Element}
 */
const SelectSingleFilter = props => {
  const {
    rootClassName,
    className,
    showAsPopup = false,
    compactMode = false,
    options,
    isNestedEnum = false,
    id,
    name,
    label,
    queryParamNames,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    getAriaLabel,
    ...rest
  } = props;

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];

  const classes = classNames(rootClassName || css.root, className);
  const labelClass = hasInitialValues ? css.labelPlainSelected : css.labelPlain;
  const labelForPlain = <span className={labelClass}>{label}</span>;

  // Check if this is a flat (non-nested) enum field for school
  const fieldKeyLower = (name || '').toLowerCase();
  const isSchoolField = fieldKeyLower === 'school' || fieldKeyLower === 'schoolname';
  const isFlatOptions = options && options.length > 0 && !options.some(opt => opt.suboptions);
  const useSimpleDropdown = isSchoolField && isFlatOptions;

  // Pass the initial values with the name key so that
  // they can be passed to the correct field
  const pickedInitialValues = {
    [name]: pickInitialValuesForFieldSelectTree(name, initialValues, isNestedEnum),
  };

  const branchPath = getBranchPath(options, pickedInitialValues[name]);
  const categorySelection = branchPath.map(option => option.label).join('/');

  const handleSubmit = queryParamNames => values => {
    const isArray = Array.isArray(queryParamNames);
    const hasMultipleQueryParams = isArray && queryParamNames.length > 1;
    const hasSingleQueryParam = isArray && queryParamNames.length === 1;
    const firstQueryParamName = isArray ? queryParamNames[0] : queryParamNames;

    // For simple dropdown fields (like school), the value comes directly
    if (useSimpleDropdown) {
      const usedValue = { [firstQueryParamName]: values?.[name] || null };
      onSubmit(usedValue);
      return;
    }

    // Nested options create multiple query params
    //   - E.g. categoryLevel1: 'v1', categoryLevel2: 'v2', categoryLevel3: 'v3'
    // If there's only a single query param in use, we flatten the value
    //   - E.g. categoryLevel: 'value'
    const usedValue = hasMultipleQueryParams
      ? queryParamNames.reduce((acc, p, i) => {
          const k = `${name}${i + 1}`;
          const v = values?.[name]?.[k] || null;
          return { ...acc, [p]: v };
        }, {})
      : hasSingleQueryParam
      ? { [firstQueryParamName]: values?.[name]?.[`${name}1`] || null }
      : values;
    onSubmit(usedValue);
  };

  // Handle initial values for simple dropdown
  const simpleDropdownInitialValues = useSimpleDropdown
    ? { [name]: initialValues && initialValues[queryParamName] }
    : pickedInitialValues;

  // Render the appropriate input component
  const renderInput = () => {
    if (useSimpleDropdown) {
      // Add "All" option for searchable select
      const optionsWithAll = [{ option: '', label: 'All' }, ...options];
      // Use different placeholder for compact mode (mobile)
      const placeholderText = compactMode ? 'All Schools' : 'Search schools';
      return (
        <FieldSearchableSelect
          name={name}
          id={`${name}-select`}
          options={optionsWithAll}
          placeholder={placeholderText}
          compactMode={compactMode}
          iconOnLeft={true}
        />
      );
    }
    return <FieldSelectTree name={name} options={options} />;
  };

  const effectiveInitialValues = useSimpleDropdown
    ? simpleDropdownInitialValues
    : pickedInitialValues;

  // Compact mode renders just the input without wrapper (for mobile inline display)
  if (compactMode && useSimpleDropdown) {
    // Need to wrap in a form to handle submission
    return (
      <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={labelForPlain}
        ariaLabel={getAriaLabel(label, categorySelection)}
        isSelected={hasInitialValues}
        id={`${id}.plain`}
        liveEdit
        onSubmit={handleSubmit(queryParamNames)}
        initialValues={effectiveInitialValues}
        hideToggle={true}
        hideClear={true}
        {...rest}
      >
        {renderInput()}
      </FilterPlain>
    );
  }

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      label={label}
      ariaLabel={getAriaLabel(label, categorySelection)}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit(queryParamNames)}
      initialValues={effectiveInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      {renderInput()}
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={labelForPlain}
      ariaLabel={getAriaLabel(label, categorySelection)}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit(queryParamNames)}
      initialValues={effectiveInitialValues}
      hideToggle={useSimpleDropdown}
      hideClear={useSimpleDropdown}
      {...rest}
    >
      {renderInput()}
    </FilterPlain>
  );
};

export default SelectSingleFilter;
